import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { forkJoin, finalize } from 'rxjs';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import {
  LlmDashboardTotals,
  LlmPdListRow,
  LlmTenantCostRow,
  LlmUsageApiService,
} from 'src/app/core/services/llm-usage-api.service';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { ACTION_BUTTON } from 'src/app/shared/utils/action-button.classes';
import { DATE_RANGE_PRESETS, compactTokens, formatUsd, rangeFor } from '../llm-cost/llm-cost.filters';
import { downloadCsv, toCsv } from '../llm-cost/csv-export.util';

/**
 * One tenant's AI spend, and the PDs behind it.
 *
 * No new endpoints: this is the dashboard and the PD list, both filtered to one tenant. The
 * totals are the same server-side aggregates the platform view uses, so a tenant page and the
 * platform page can never disagree.
 */
@Component({
  selector: 'app-llm-tenant-cost',
  standalone: true,
  imports: [CommonModule, RouterLink, PageSurfaceHeaderComponent],
  templateUrl: './llm-tenant-cost.component.html',
})
export class LlmTenantCostComponent implements OnInit {
  private readonly api = inject(LlmUsageApiService);
  private readonly route = inject(ActivatedRoute);

  readonly btn = ACTION_BUTTON;
  readonly presets = DATE_RANGE_PRESETS;
  readonly formatUsd = formatUsd;
  readonly compactTokens = compactTokens;

  tenantId = 0;
  selectedPreset = '30d';
  loading = false;
  error = '';

  totals: LlmDashboardTotals | null = null;
  summary: LlmTenantCostRow | null = null;
  pds: LlmPdListRow[] = [];
  meta = { total: 0, page: 1, limit: 25, pages: 0 };

  ngOnInit(): void {
    this.tenantId = Number(this.route.snapshot.paramMap.get('tenantId'));
    this.load();
  }

  onPresetChange(key: string): void {
    this.selectedPreset = key;
    this.load();
  }

  load(page = 1): void {
    const preset = this.presets.find((p) => p.key === this.selectedPreset) ?? this.presets[1];
    const range = rangeFor(preset.days);
    const filters = { ...range, tenant_id: this.tenantId };

    this.loading = true;
    this.error = '';
    forkJoin({
      dashboard: this.api.dashboard(filters),
      tenants: this.api.tenants(range),
      // A tenant's own page lists its PDs newest first, not dearest first.
      pds: this.api.pdList({ ...filters, page, limit: this.meta.limit, sort: 'recent' }),
    })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          this.totals = res.dashboard.data?.totals ?? null;
          this.summary = (res.tenants.data ?? []).find((t) => t.tenant_id === this.tenantId) ?? null;
          this.pds = res.pds.data ?? [];
          this.meta = res.pds.meta ?? this.meta;
        },
        error: (err) => (this.error = resolveApiErrorMessage(err) || 'Could not load this tenant.'),
      });
  }

  get tenantName(): string {
    return this.summary?.tenant_name ?? `Tenant ${this.tenantId}`;
  }

  exportPds(): void {
    const headers = [
      { key: 'session_code', label: 'PD Link' },
      { key: 'agent_id', label: 'Agent' },
      { key: 'calls', label: 'LLM Calls' },
      { key: 'input_tokens', label: 'Input Tokens' },
      { key: 'output_tokens', label: 'Output Tokens' },
      { key: 'total_tokens', label: 'Total Tokens' },
      { key: 'total_cost_usd', label: 'Cost (USD)' },
      { key: 'last_at', label: 'Last Call' },
    ];
    downloadCsv(
      `llm-tenant-${this.tenantId}-pds.csv`,
      toCsv(headers, this.pds as unknown as Record<string, unknown>[]),
    );
  }
}
